// @flow
import {
  selectCurrentCurrency,
  selectIsAuthenticatedUser,
  selectTaxesDisplaySetting,
  selectUserCountryCode,
} from '@gandi/authentication';
import {
  selectCurrency as selectPrepaidCurrency,
  selectDefaultCurrency as selectDefaultBUCurrency,
} from '@gandi/billing';
import { selectIsLoadedOneOrganization } from '@gandi/redux-organization';
import { selectCurrencies } from '@gandi/redux-places';

import type { FullState } from '../../types';
import {
  selectOrgBU,
  selectOrgCountry,
} from '../components/PreferencesWidget/modules/selectors';

export function selectIsAnonymous(state: FullState): boolean {
  return !selectIsAuthenticatedUser(state);
}

/**
 * fresh user is a user without a prepaid
 */
export function selectIsFreshUser(
  state: FullState,
  orgId: string,
): void | boolean {
  const isAuthenticatedUser = selectIsAuthenticatedUser(state);

  if (!isAuthenticatedUser) {
    return false;
  }

  const isLoadedOrg = selectIsLoadedOneOrganization(state, orgId);

  if (!isLoadedOrg) {
    return undefined;
  }

  const orgBU = selectOrgBU(state, orgId);

  return !orgBU;
}

type SelectCountryArgs = {
  locationQueryCountry?: string,
  orgId?: string,
};

/**
 * Country logic in the shop
 */
export function selectCountry(
  state: FullState,
  { locationQueryCountry, orgId = '' }: SelectCountryArgs,
): string {
  const userCountryCode = selectUserCountryCode(state);
  const orgCountry = selectOrgCountry(state, orgId);
  return orgCountry || locationQueryCountry || userCountryCode;
}

type SelectCurrencyArgs = {
  locationQueryCurrency?: string,
  country: string,
  orgId?: string,
};

export function selectCurrency(
  state: FullState,
  { locationQueryCurrency, country, orgId = '' }: SelectCurrencyArgs,
): string {
  const prepaidCurrency = selectPrepaidCurrency(state, {
    sharingId: orgId,
  });
  const defaultBUCurrency = selectDefaultBUCurrency(state, {
    sharingId: orgId,
    country,
  });

  const currencies = selectCurrencies(state, country);
  const consolidatedLocCurrency = currencies.find(
    currency => locationQueryCurrency === currency,
  );

  // Last fallback
  const currentCurrency = selectCurrentCurrency(state);

  return (
    prepaidCurrency ||
    consolidatedLocCurrency ||
    defaultBUCurrency ||
    currencies?.[0] ||
    currentCurrency
  );
}

export function selectTaxesIncluded(
  state: FullState,
  locationQueryTaxes?: string,
): boolean {
  const taxesDisplaySetting = selectTaxesDisplaySetting(state);
  return locationQueryTaxes
    ? locationQueryTaxes === 'yes'
    : taxesDisplaySetting;
}
